import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    TrashIcon,
    MapPinIcon,
    XMarkIcon,
    LinkIcon,
    PhoneIcon,
    PencilSquareIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminLocations = () => {
    const [locations, setLocations] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        google_maps_link: '',
        embed_link: '',
        phone: ''
    });

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await axios.get(`${API}/locations`);
            setLocations(response.data);
        } catch (error) {
            console.error('Failed to fetch locations:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this location?")) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/locations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Location deleted successfully!');
            fetchLocations();
        } catch (error) {
            toast.error('Failed to delete location');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (loc) => {
        setFormData({
            name: loc.name,
            address: loc.address,
            google_maps_link: loc.google_maps_link,
            embed_link: loc.embed_link || '',
            phone: loc.phone || ''
        });
        setEditingId(loc.id);
        setShowForm(true);
    };

    // Robust HTML entity decoder
    const decodeHTMLEntities = (text) => {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = text;
        return textArea.value;
    };

    const cleanupEmbedLink = (val) => {
        if (!val) return '';
        // If it's a full iframe tag, extract the src
        if (val.includes('<iframe')) {
            const match = val.match(/src=["']([^"']+)["']/i);
            if (match && match[1]) {
                val = match[1];
            }
        }
        // Always decode entities because URLs from Google Maps often contain encoded chars
        return decodeHTMLEntities(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const dataToSubmit = {
                ...formData,
                embed_link: cleanupEmbedLink(formData.embed_link)
            };

            if (editingId) {
                await axios.put(`${API}/locations/${editingId}`, dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Location updated successfully!');
            } else {
                await axios.post(`${API}/locations`, dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Location added successfully!');
            }
            resetForm();
            fetchLocations();
        } catch (error) {
            toast.error(editingId ? 'Failed to update location' : 'Failed to add location');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', address: '', google_maps_link: '', embed_link: '', phone: '' });
        setShowForm(false);
        setEditingId(null);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
            <div className="p-4 lg:p-8">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Store Locations</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage your physical store outlets</p>
                    </div>
                    {!showForm && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                setEditingId(null);
                                setFormData({ name: '', address: '', google_maps_link: '', embed_link: '', phone: '' });
                                setShowForm(true);
                            }}
                            className="bg-gray-900 text-white px-4 py-2 rounded-xl hover:bg-black transition-all shadow-sm flex items-center text-sm font-medium"
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Location
                        </motion.button>
                    )}
                </div>

                <AnimatePresence>
                    {showForm && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden mb-8"
                        >
                            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-inner">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                                        {editingId ? 'Edit Store Location' : 'New Store Location'}
                                    </h3>
                                    <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Store Name</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="e.g., Downtown Outlet"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Phone Number</label>
                                            <input
                                                type="text"
                                                value={formData.phone}
                                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="e.g., +91 98765 43210"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Address</label>
                                        <textarea
                                            required
                                            value={formData.address}
                                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="Full store address"
                                            rows={2}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Google Maps Link (Navigation)</label>
                                            <div className="relative">
                                                <LinkIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                <input
                                                    type="url"
                                                    required
                                                    value={formData.google_maps_link}
                                                    onChange={e => setFormData({ ...formData, google_maps_link: e.target.value })}
                                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="https://goo.gl/maps/..."
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Embed Map Link (Optional)</label>
                                            <div className="relative">
                                                <MapPinIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                <input
                                                    type="text"
                                                    value={formData.embed_link}
                                                    onChange={e => {
                                                        const cleanVal = cleanupEmbedLink(e.target.value);
                                                        setFormData({ ...formData, embed_link: cleanVal });
                                                    }}
                                                    className={`w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm ${formData.embed_link && !formData.embed_link.includes('google.com/maps/embed') ? 'border-amber-500' : 'border-gray-200'}`}
                                                    placeholder="Paste full iframe code or src URL"
                                                />
                                            </div>
                                            {formData.embed_link && formData.embed_link.includes('google.com/maps/embed') && (
                                                <div className="mt-3">
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Live Preview:</p>
                                                    <div className="rounded-xl overflow-hidden border border-gray-200 aspect-video bg-gray-100">
                                                        <iframe
                                                            src={formData.embed_link}
                                                            width="100%"
                                                            height="100%"
                                                            style={{ border: 0 }}
                                                            title="Admin Preview"
                                                        ></iframe>
                                                    </div>
                                                </div>
                                            )}
                                            {formData.embed_link && !formData.embed_link.includes('google.com/maps/embed') && (
                                                <p className="mt-1 text-[10px] text-amber-600 font-medium italic">
                                                    Warning: Please use the 'Embed a map' link from Google Maps.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-end space-x-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-black transition-colors text-sm font-medium disabled:opacity-50"
                                        >
                                            {loading ? 'Saving...' : (editingId ? 'Update Location' : 'Save Location')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {locations.map((loc) => (
                        <div key={loc.id} className="border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-shadow relative group bg-white shadow-sm flex flex-col">
                            <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleEdit(loc)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit"
                                >
                                    <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(loc.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2 flex items-center pr-12">
                                <MapPinIcon className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                                {loc.name}
                            </h3>
                            <p className="text-gray-500 text-sm mb-4 line-clamp-2">{loc.address}</p>
                            {loc.phone && (
                                <p className="text-gray-600 text-sm mb-4 flex items-center">
                                    <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                                    {loc.phone}
                                </p>
                            )}
                            <div className="mt-auto">
                                <a
                                    href={loc.google_maps_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-bold text-gray-900 uppercase tracking-widest hover:text-gray-600 transition-colors border-b border-gray-900 pb-0.5"
                                >
                                    View on Maps
                                </a>
                            </div>
                        </div>
                    ))}
                </div>

                {locations.length === 0 && !showForm && (
                    <div className="text-center py-12">
                        <MapPinIcon className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-400">No stores added yet.</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AdminLocations;
